import { useEffect } from 'react';

/**
 * VERIFICADOR DA R01 (p.28), em desenvolvimento: **a página não rola** — nem
 * de lado, nem para baixo. Quem rola é o quadro da tabela e a área de
 * conteúdo, por dentro. Quando o documento fica maior que a janela, o console
 * diz em qual eixo, quanto, e quem está na ponta.
 *
 * ## Por que os dois eixos, e por que isto existe
 *
 * O defeito é silencioso e sempre o mesmo: um elemento **absoluto sem pai
 * posicionado** escapa do rolador — ele se ancora na casca, que não corta — e
 * estica o documento. Ninguém vê um elemento invisível; o console vê a conta.
 *
 *   - **De lado:** a tabela da Manutenção tinha a própria rolagem e mesmo
 *     assim a página rolava 347 px a 1024, empurrada por um `sr-only` de 1 px.
 *   - **Para baixo (25/10/2026):** a etiqueta de verificação do lançamento
 *     rápido levava um `sr-only` com o texto inteiro da verificação, e o
 *     documento passou a rolar 149 px — **com dois cortes de rolagem na tela
 *     ao mesmo tempo**, e o cabeçalho e o menu saindo de vista ao rolar. Eram
 *     os dois o mesmo erro, e o verificador só olhava um eixo.
 *
 * A casca é `h-full` com rolagem por dentro justamente para o cabeçalho e o
 * menu ficarem parados. Documento que rola desmancha isso, e é por isso que a
 * conta é zero, e não "pouco".
 *
 * Confere depois de cada troca de tela (com tempo para o dado chegar) e a cada
 * mudança de tamanho da janela.
 */
export function useSemRolagemLateral(caminho: string): void {
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let relogio = window.setTimeout(conferir, 1500);

    function naPonta(eixo: 'largura' | 'altura'): string {
      const raiz = document.documentElement;
      const limite = eixo === 'largura' ? raiz.scrollWidth : raiz.scrollHeight;
      return (
        [...document.querySelectorAll<HTMLElement>('body *')]
          .filter((elemento) => {
            const caixa = elemento.getBoundingClientRect();
            const ponta = eixo === 'largura' ? caixa.right : caixa.bottom + window.scrollY;
            return Math.round(ponta) >= limite - 1;
          })
          .slice(-3)
          .map((elemento) => `${elemento.tagName.toLowerCase()}.${[...elemento.classList].slice(0, 4).join('.')}`)
          .join(' | ') || 'nenhum elemento achado'
      );
    }

    function conferir() {
      const raiz = document.documentElement;
      const deLado = raiz.scrollWidth - raiz.clientWidth;
      const paraBaixo = raiz.scrollHeight - raiz.clientHeight;
      if (deLado > 0) {
        console.warn(
          `R01 (p.28): a página ${caminho} rola ${String(deLado)} px de lado a ${String(raiz.clientWidth)} px. Na ponta: ${naPonta('largura')}.`,
        );
      }
      if (paraBaixo > 0) {
        /* Dois cortes de rolagem na mesma tela, e o cabeçalho e o menu saindo
           de vista: é sempre alguém absoluto que escapou do rolador. */
        console.warn(
          `R01 (p.28): a página ${caminho} rola ${String(paraBaixo)} px para baixo — o documento não pode rolar, só a área de conteúdo. Na ponta: ${naPonta('altura')}.`,
        );
      }
    }

    function aoMudarLargura() {
      window.clearTimeout(relogio);
      relogio = window.setTimeout(conferir, 500);
    }
    window.addEventListener('resize', aoMudarLargura);
    return () => {
      window.clearTimeout(relogio);
      window.removeEventListener('resize', aoMudarLargura);
    };
  }, [caminho]);
}
